<template>
    <div class="vrm-modal vrm-modal--wide vrm-modal--bulk-alarms mfp-with-anim">
        <div class="vrm-modal__header">
            <div class="vrm-modal__title">
                {{
                    $t('alarms.alarm_bulk_modal_title', {
                        i18nFallback: 'Add alarm rules to installations',
                    })
                }}
            </div>
            <p>
                {{
                    $t('alarms.only_installations_with_same_device_type', {
                        i18nFallback:
                            'This list only includes installations that have same device type as the selected installation.',
                    })
                }}
            </p>
        </div>
        <div class="vrm-modal__content">
            <div class="vrm-modal__content-searchbox">
                <span class="sg-vrm-icon_040_search-16px"></span>
                <input
                    v-model="searchTerm"
                    type="text"
                    class="vrm-modal__content-searchbox-input"
                    :placeholder="$t('alarms.search_placeholder', { i18nFallback: 'Search' })"
                />
            </div>
            <div class="vrm-modal__content-selected">
                <div>
                    {{
                        $t('alarms.selected_installations', {
                            i18nFallback: 'Selected Installations and installation groups',
                        })
                    }}
                    ({{ selectedInstallationObjects.length }})
                </div>
                <div class="vrm-modal__content-selected-tags">
                    <div
                        v-for="item in selectedInstallationObjects"
                        :key="item.id || item.idSiteGroup"
                        class="selected-tag"
                    >
                        <span class="selected-tag__avatar">
                            <VrmInstallationAvatar :installation="item" />
                        </span>
                        <span class="selected-tag__name">
                            {{ truncateInstallationName(item.name) }}
                        </span>
                        <span v-if="item.idSiteGroup">
                            ({{ numberOfInstallationsWithSameDevice(item) }})
                        </span>
                        <span
                            v-if="
                                item.id !== selectedInstallation &&
                                    !isInstallationFromSelectedGroup(item)
                            "
                            class="selected-tag__remove sg-vrm-icon_055_cross-16px"
                            @click="removeInstallation(item)"
                        ></span>
                        <i
                            v-if="isInstallationFromSelectedGroup(item)"
                            v-tooltip="
                                $t('alarms.installation_is_part_of_group', {
                                    i18nFallback:
                                        'This installation is part of a selected installation group.',
                                })
                            "
                            class="sg-vrm-icon_061_info-round-16px"
                        />
                    </div>
                </div>
            </div>
            <div v-if="loading" class="loading">
                <vrm-loading />
            </div>
            <div v-else class="vrm-modal__content-list">
                <div class="vrm-modal__content-list-title">
                    <input
                        type="checkbox"
                        class="vrm-modal__content-list-checkbox"
                        :checked="isAllSelected"
                        :indeterminate.prop="isIndeterminate"
                        :disabled="filteredInstallations.length === 0"
                        @change="selectAll"
                    />
                    <span>{{ $t('groups.installations', { i18nFallback: 'Installations' }) }}</span>
                </div>
                <p
                    v-if="filteredInstallations.length === 0"
                    class="vrm-modal__content-list__no-results"
                >
                    {{ $t('alarms.no_results_found', { i18nFallback: 'No results found' }) }}
                </p>
                <ul v-else>
                    <li v-for="(item, index) in filteredInstallations" :key="index">
                        <span>
                            <input
                                v-model="selectedInstallations"
                                type="checkbox"
                                class="vrm-modal__content-list-checkbox"
                                :value="item.id || item.idSiteGroup"
                                :disabled="
                                    item.id === selectedInstallation ||
                                        isInstallationFromSelectedGroup(item)
                                "
                                @change="onItemChange(item, $event)"
                            />
                        </span>
                        <span v-if="item.idSiteGroup">
                            {{
                                $t('alarms.installation_group', {
                                    i18nFallback: 'Installation group',
                                })
                            }}
                            -
                        </span>
                        <span class="vrm-modal__content-list-item">{{ item.name }}</span>
                        <span v-if="item.idSiteGroup">
                            ({{ numberOfInstallationsWithSameDevice(item) }})
                        </span>
                        <i
                            v-if="isInstallationFromSelectedGroup(item)"
                            v-tooltip="
                                $t('alarms.installation_is_part_of_group', {
                                    i18nFallback:
                                        'This installation is part of a selected installation group.',
                                })
                            "
                            class="sg-vrm-icon_061_info-round-16px"
                        />
                    </li>
                </ul>
            </div>
        </div>
        <div class="vrm-modal__footer">
            <div>
                <label v-if="alarmDeviceType === DEVICE_TYPES.FLUID_LEVEL">
                    <input v-model="applyToSameTypeTanks" type="checkbox" />
                    {{
                        $t('alarms.only_apply_tanks', {
                            i18nFallback:
                                'Only apply this alarm to tanks that have the same fluid type',
                        })
                    }}
                </label>
                <label v-if="alarmDeviceType === DEVICE_TYPES.TEMPERATURE_SENSOR">
                    <input v-model="applyToSameTypeTemperatureSensors" type="checkbox" />
                    {{
                        $t('alarms.only_apply_sensors', {
                            i18nFallback:
                                'Only apply this alarm to temperature sensors that have the same type',
                        })
                    }}
                </label>
            </div>
            <div class="vrm-modal__footer-buttons">
                <button class="vrm-button vrm-button--secondary" @click="onCancel">
                    {{ $t('alarms.back', { i18nFallback: 'Back' }) }}
                </button>
                <button
                    id="button-save-batch-alarms"
                    class="vrm-button vrm-button--primary"
                    @click="saveAlarm"
                >
                    {{ $t('alarms.save_alarm', { i18nFallback: 'Save alarm' }) }}
                </button>
            </div>
        </div>
        <modal-close-handle />
    </div>
</template>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { ngInject } from '@/vue/decorators/angular-interop';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmInstallationAvatar from '../shared/VrmInstallationAvatar.vue';
import { DEVICE_TYPES } from '@/constants/deviceTypes';
import VrmInfoTooltip from '@/vue/components/shared/VrmInfoTooltip';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
} from '@/vue/store/modules/notifications';
import { vrmInstallationAccessLevel } from '@/vue/store/modules/installation-access-level';
import VrmLoading from '@/vue/components/shared/VrmLoading.vue';

@Component({
    components: {
        ModalCloseHandle,
        VrmInstallationAvatar,
        VrmInfoTooltip,
        VrmLoading,
    },
})
@AngularLocaleBundle('alarms', 'groups')
export default class AddAlarmsBulkModal extends Vue {
    @Prop({ type: Array }) groups;
    @Prop({ type: Number }) selectedInstallation;
    @Prop({ type: Number }) alarmDeviceType;
    @Prop({ type: Object }) selectedModel;

    @ngInject()
    ApiService;
    @ngInject()
    vrmNotificationsService;

    DEVICE_TYPES = DEVICE_TYPES;
    vrmInstallationAccessLevel = vrmInstallationAccessLevel;

    searchTerm = '';
    selectedInstallations = [];
    applyToSameTypeTanks = false;
    applyToSameTypeTemperatureSensors = false;

    localInstallations = []; // Store locally to prevent reactive updates from service

    loading = true;

    async mounted() {
        this.selectedInstallations.push(this.selectedInstallation);
        this.loading = true;
        try {
            const response = await this.ApiService.users('installations?extended=1', []);
            if (response.data.success) {
                // Store locally to prevent reactive updates from service
                this.localInstallations = response.data.records.map(installation => ({
                    ...installation,
                    id: installation.idSite,
                }));
            }
        } catch (error) {
            console.error('Failed to fetch installations:', error);
        } finally {
            this.loading = false;
        }
    }

    get isAllSelected() {
        // Count how many are checked
        const checkedCount = this.filteredInstallations.filter(item =>
            this.selectedInstallations.includes(item.id || item.idSiteGroup),
        ).length;

        return this.filteredInstallations.length === checkedCount;
    }

    get isIndeterminate() {
        return this.selectedInstallations.length > 0 && !this.isAllSelected;
    }

    get allAvailableItems() {
        // Filter installations based on the selected device type
        let installationsWithSameDevice = this.localInstallations.filter(
            installation =>
                // Include if it's the selected installation OR has matching device type
                installation.id === this.selectedInstallation ||
                (installation.extended &&
                    installation.extended
                        .map(extended => extended.code)
                        .includes(this.selectedModel.attribute.code)),
        );

        let installationsWithEnoughPermissions = installationsWithSameDevice.filter(
            installation =>
                installation.id === this.selectedInstallation ||
                installation.accessLevel === this.vrmInstallationAccessLevel.FULL_CONTROL ||
                installation.accessLevel === this.vrmInstallationAccessLevel.TECHNICIAN,
        );

        let groupsThatAreNotEmpty = this.groups.filter(
            group =>
                group.sites &&
                group.sites.filter(site =>
                    installationsWithEnoughPermissions.some(
                        installation => installation.id === site.idSite,
                    ),
                ).length > 0,
        );

        return [...installationsWithEnoughPermissions, ...groupsThatAreNotEmpty];
    }

    get filteredInstallations() {
        if (!this.searchTerm) {
            return this.allAvailableItems;
        }
        return this.allAvailableItems.filter(item =>
            item.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
        );
    }

    get selectedInstallationObjects() {
        return this.allAvailableItems.filter(item =>
            this.selectedInstallations.includes(item.id || item.idSiteGroup),
        );
    }

    selectAll(event) {
        if (event.target.checked) {
            // Get IDs from filtered installations
            const filteredIds = [];

            this.filteredInstallations.forEach(item => {
                // Add direct installation or group ID
                filteredIds.push(item.id || item.idSiteGroup);

                // If it's a group, add all its installations
                if (item.idSiteGroup && item.sites) {
                    item.sites.forEach(site => {
                        filteredIds.push(site.idSite);
                    });
                }
            });

            // Combine with existing selections, excluding duplicates
            const allIds = [...new Set([...this.selectedInstallations, ...filteredIds])];

            this.selectedInstallations = allIds;
        } else {
            this.filteredInstallations.forEach(item => {
                if (item.idSiteGroup && item.sites) {
                    // Remove the group ID itself
                    this.selectedInstallations = this.selectedInstallations.filter(
                        id => id !== item.idSiteGroup,
                    );
                    // If it's a group, remove all installations from that group
                    const siteIds = item.sites.map(site => site.idSite);
                    this.selectedInstallations = this.selectedInstallations.filter(
                        id => id === this.selectedInstallation || !siteIds.includes(id),
                    );
                } else {
                    // If it's an individual installation, just remove it
                    this.selectedInstallations = this.selectedInstallations.filter(
                        id => id === this.selectedInstallation || id !== item.id,
                    );
                }
            });
        }
    }

    removeInstallation(tagItem) {
        if (tagItem.idSiteGroup) {
            // If it's a group, remove all installations from that group
            if (tagItem.sites) {
                const siteIds = tagItem.sites.map(site => site.idSite);
                // Filter out group
                this.selectedInstallations = this.selectedInstallations.filter(
                    id => id !== tagItem.idSiteGroup,
                );
                // Also remove all sites from this group
                this.selectedInstallations = this.selectedInstallations.filter(
                    id => id === this.selectedInstallation || !siteIds.includes(id),
                );
            }
        } else {
            this.selectedInstallations = this.selectedInstallations.filter(
                item => item !== tagItem.id,
            );
        }
    }

    onCancel() {
        this.$emit('close');
    }

    truncateInstallationName(name) {
        return name.length > 40 ? name.slice(0, 40) + '…' : name;
    }

    get uniqueInstallationIds() {
        return this.collectInstallationIds();
    }

    collectInstallationIds() {
        let installationIds = [];
        // Collect installation IDs from selected installations
        this.selectedInstallationObjects.forEach(item => {
            if (item.idSiteGroup) {
                // If it's a group extract installation IDs from sites array
                if (item.sites) {
                    const groupInstallationIds = item.sites.map(site => site.idSite);
                    installationIds.push(...groupInstallationIds);
                }
            } else {
                // If it's an installation - add id directly
                installationIds.push(item.id);
            }
        });

        // Remove duplicates
        return [...new Set(installationIds)];
    }

    saveAlarm() {
        // Prepare complete data object to send to the backend
        let payload = {
            siteIds: this.uniqueInstallationIds.filter(id => id !== this.selectedInstallation), // Exclude the selected installation
            alarmData: {
                idSite: this.selectedInstallation,
                instance: this.selectedModel.device.instance,
                idDataAttribute: this.selectedModel.attribute.idDataAttribute,
            },
        };

        if (this.selectedModel.attribute.dataType == 'float') {
            payload.alarmData.lowAlarm = this.selectedModel.lowAlarm;
            payload.alarmData.lowAlarmHysteresis = this.selectedModel.lowAlarmHysteresis;
            payload.alarmData.highAlarm = this.selectedModel.highAlarm;
            payload.alarmData.highAlarmHysteresis = this.selectedModel.highAlarmHysteresis;
            payload.alarmData.NotifyAfterSeconds = this.selectedModel.timeout;
            payload.alarmData.AlarmEnabled = 1;
        } else if (this.selectedModel.attribute.dataType == 'enum') {
            payload.alarmData.enumValues = [];

            for (var key in this.selectedModel.toggles) {
                if (!this.selectedModel.toggles[key]) {
                    payload.alarmData.enumValues.push(parseInt(key));
                }
            }

            payload.alarmData.notifyAfterSeconds = this.selectedModel.timeout;
            payload.alarmData.alarmEnabled = 1;
        }

        const applyToSameSubType =
            this.applyToSameTypeTanks || this.applyToSameTypeTemperatureSensors;

        // TODO: Send to API
        this.ApiService.addAlarmsInBulk(this.selectedInstallation, payload, applyToSameSubType)
            .then(response => {
                // Stores the response data to check if some alarms already exist
                const repeatedAlarms = response.data.existingAlarms;
                // Stores the response data to check if some alarms are invalid
                const invalidAlarms = response.data.invalidAlarms;

                // If no alarms were repeated or invalid
                if (repeatedAlarms.length === 0 && invalidAlarms.length === 0) {
                    this.$emit('close');
                    this.$router.push({
                        name: 'base.installation.settings.alarms',
                        params: { id: this.selectedInstallation },
                    });
                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_SUCCESS,
                        message: this.$t('alarms.alarms_added_successfully'),
                    });
                    // If there are repeated alarms
                } else if (repeatedAlarms.length > 0 && invalidAlarms.length === 0) {
                    this.$emit('close');
                    this.$router.push({
                        name: 'base.installation.settings.alarms',
                        params: { id: this.selectedInstallation },
                    });
                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_WARNING,
                        message: `${this.$t('alarms.some_installations_already_have_alarm')}`,
                    });
                    // If there are invalid alarms
                } else if (invalidAlarms.length > 0 && repeatedAlarms.length === 0) {
                    this.$emit('close');
                    this.$router.push({
                        name: 'base.installation.settings.alarms',
                        params: { id: this.selectedInstallation },
                    });
                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_WARNING,
                        message: `${this.$t('alarms.some_installations_have_invalid_alarms')}`,
                    });
                    // If there are both repeated and invalid alarms
                } else if (repeatedAlarms.length > 0 && invalidAlarms.length > 0) {
                    this.$emit('close');
                    this.$router.push({
                        name: 'base.installation.settings.alarms',
                        params: { id: this.selectedInstallation },
                    });
                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_WARNING,
                        message: `${this.$t(
                            'alarms.some_installations_already_have_alarm',
                        )} ${this.$t('alarms.error_adding_alarms')}`,
                    });
                }
            })
            .catch(() => {
                this.$emit('close');
            });
    }

    onItemChange(item, event) {
        // If it's a group being selected
        if (item.idSiteGroup && event.target.checked) {
            // Add all installations from the group to selectedInstallations
            if (item.sites) {
                item.sites.forEach(site => {
                    if (!this.selectedInstallations.includes(site.idSite)) {
                        this.selectedInstallations.push(site.idSite);
                    }
                });
            }
        }
        // If it's a group being deselected
        else if (item.idSiteGroup && !event.target.checked) {
            // Remove all installations from the group
            if (item.sites) {
                const siteIds = item.sites.map(site => site.idSite);
                this.selectedInstallations = this.selectedInstallations.filter(
                    id => id === this.selectedInstallation || !siteIds.includes(id),
                );
            }
        }
    }

    isInstallationFromSelectedGroup(item) {
        // If it's not an individual installation, return false
        if (item.idSiteGroup || !item.id) {
            return false;
        }

        // Check if this installation belongs to any selected group
        return (
            this.groups &&
            this.groups.some(
                group =>
                    this.selectedInstallations.includes(group.idSiteGroup) &&
                    group.sites &&
                    group.sites.some(site => site.idSite === item.id),
            )
        );
    }

    numberOfInstallationsWithSameDevice(group) {
        return group.sites.filter(site =>
            this.allAvailableItems
                .filter(item => !item.idSiteGroup) // Only check actual installations, not groups
                .some(installation => installation.id === site.idSite),
        ).length;
    }
}
</script>
